/**
 * Stock Item Delete Dialog Component
 * 
 * Provides a comprehensive delete confirmation dialog with:
 * - Validation checks
 * - Blocking reasons display
 * - Warnings
 * - Detailed item information
 */

import { useState, useEffect } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle, XCircle, Info, Package, TrendingUp, Calendar } from "lucide-react";
import { StockItem } from "@/types/stock-item";
import {
  validateStockItemDeletion,
  validateBulkStockItemDeletion,
  getBulkDeleteValidationSummary,
  DeleteValidationResult,
} from "@/lib/stock-item-delete-validation";

interface StockItemDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: StockItem[];
  onConfirmDelete: (itemCodes: string[]) => void;
}

export function StockItemDeleteDialog({
  open,
  onOpenChange,
  items,
  onConfirmDelete,
}: StockItemDeleteDialogProps) {
  const [validationResults, setValidationResults] = useState<Map<string, DeleteValidationResult>>(
    new Map()
  );
  const [summary, setSummary] = useState<ReturnType<typeof getBulkDeleteValidationSummary> | null>(
    null
  );

  // Validate items when dialog opens or items change
  useEffect(() => {
    if (open && items.length > 0) {
      const results = validateBulkStockItemDeletion(items);
      setValidationResults(results);
      setSummary(getBulkDeleteValidationSummary(results));
    }
  }, [open, items]);

  const handleConfirm = () => {
    // Only delete items that passed validation
    const itemsToDelete = items
      .filter((item) => validationResults.get(item.code)?.canDelete)
      .map((item) => item.code);

    if (itemsToDelete.length > 0) {
      onConfirmDelete(itemsToDelete);
    }
    onOpenChange(false);
  };

  const isSingleItem = items.length === 1;
  const singleItem = isSingleItem ? items[0] : null;
  const singleValidation = singleItem ? validationResults.get(singleItem.code) : null;

  const canProceed = summary ? summary.canDeleteCount > 0 : false;
  const allBlocked = summary ? summary.blockedCount === summary.totalItems : false;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {allBlocked ? (
              <>
                <XCircle className="h-5 w-5 text-destructive" />
                Cannot Delete Stock Item{items.length > 1 ? "s" : ""}
              </>
            ) : (
              <>
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Stock Item{items.length > 1 ? "s" : ""}?
              </>
            )}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isSingleItem ? (
              <span>
                You are about to delete <strong>{singleItem?.code}</strong>
              </span>
            ) : (
              <span>
                You are about to delete <strong>{items.length} items</strong>
              </span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-4">
            {/* Single Item View */}
            {isSingleItem && singleItem && singleValidation && (
              <div className="space-y-4">
                {/* Item Details */}
                <div className="rounded-lg border bg-muted/50 p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="font-semibold">{singleItem.code}</span>
                        <Badge variant={singleItem.inactive ? "secondary" : "default"}>
                          {singleItem.inactive ? "Inactive" : "Active"}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{singleItem.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                        <span>Category: {singleItem.category}</span>
                        <span>•</span>
                        <span>Qty: {singleItem.physicalQuantity} {singleItem.unitOfMeasure}</span>
                        {singleItem.allocatedQuantity > 0 && (
                          <>
                            <span>•</span>
                            <span className="text-orange-600 font-medium">
                              Allocated: {singleItem.allocatedQuantity}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Blocking Reasons */}
                {singleValidation.blockingReasons.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-destructive">
                      <XCircle className="h-4 w-4" />
                      <span className="font-semibold text-sm">Cannot Delete - Blocking Issues</span>
                    </div>
                    <div className="space-y-2 rounded-lg border border-destructive/50 bg-destructive/5 p-3">
                      {singleValidation.blockingReasons.map((reason, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <span className="text-destructive mt-0.5">•</span>
                          <span className="text-destructive">{reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Warnings */}
                {singleValidation.warnings.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-orange-600">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-semibold text-sm">Warnings</span>
                    </div>
                    <div className="space-y-2 rounded-lg border border-orange-200 bg-orange-50 p-3">
                      {singleValidation.warnings.map((warning, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <span className="text-orange-600 mt-0.5">⚠</span>
                          <span className="text-orange-900">{warning}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {singleValidation.canDelete && singleValidation.warnings.length === 0 && (
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <div className="flex items-center gap-2 text-green-700">
                      <Info className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        This item can be safely deleted.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bulk Items View */}
            {!isSingleItem && summary && (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-lg border bg-muted/50 p-3 text-center">
                    <div className="text-2xl font-bold">{summary.totalItems}</div>
                    <div className="text-xs text-muted-foreground">Total Items</div>
                  </div>
                  <div className="rounded-lg border bg-green-50 p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">
                      {summary.canDeleteCount}
                    </div>
                    <div className="text-xs text-green-700">Can Delete</div>
                  </div>
                  <div className="rounded-lg border bg-destructive/5 p-3 text-center">
                    <div className="text-2xl font-bold text-destructive">
                      {summary.blockedCount}
                    </div>
                    <div className="text-xs text-destructive">Blocked</div>
                  </div>
                </div>

                {/* Common Blocking Reasons */}
                {summary.commonBlockingReasons.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-destructive">
                      <XCircle className="h-4 w-4" />
                      <span className="font-semibold text-sm">Common Issues</span>
                    </div>
                    <div className="space-y-1 rounded-lg border border-destructive/50 bg-destructive/5 p-3">
                      {summary.commonBlockingReasons.map((reason, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm">
                          <span className="text-destructive mt-0.5">•</span>
                          <span className="text-destructive">{reason}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Item List */}
                <div className="space-y-2">
                  <span className="text-sm font-semibold">Items to Delete:</span>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {items.map((item) => {
                      const validation = validationResults.get(item.code);
                      const canDelete = validation?.canDelete ?? false;
                      const hasWarnings = (validation?.warnings.length ?? 0) > 0;

                      return (
                        <div
                          key={item.code}
                          className={`flex items-center justify-between rounded-lg border p-2 ${
                            canDelete
                              ? "bg-green-50 border-green-200"
                              : "bg-destructive/5 border-destructive/50"
                          }`}
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Package className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-sm truncate">{item.code}</div>
                              <div className="text-xs text-muted-foreground truncate">
                                {item.description}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {canDelete ? (
                              <>
                                {hasWarnings && (
                                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Warning
                                  </Badge>
                                )}
                                <Badge variant="outline" className="text-green-700 border-green-700">
                                  Can Delete
                                </Badge>
                              </>
                            ) : (
                              <Badge variant="destructive">
                                <XCircle className="h-3 w-3 mr-1" />
                                Blocked
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Partial Delete Warning */}
                {summary.blockedCount > 0 && summary.canDeleteCount > 0 && (
                  <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-orange-900">
                        <strong>{summary.canDeleteCount}</strong> of <strong>{summary.totalItems}</strong> items will be deleted.{" "}
                        <strong>{summary.blockedCount}</strong> item(s) cannot be deleted due to validation errors.
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Alternative Action Suggestion */}
            {allBlocked && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900">
                    <strong>Alternative:</strong> Consider marking these items as{" "}
                    <strong>Inactive</strong> instead of deleting them. This preserves historical
                    data while removing them from active use.
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {canProceed && (
            <AlertDialogAction
              onClick={handleConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {allBlocked
                ? "Cannot Delete"
                : isSingleItem
                ? "Delete Item"
                : `Delete ${summary?.canDeleteCount} Item${summary?.canDeleteCount !== 1 ? "s" : ""}`}
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
